import { useCallback, useEffect, useRef, useState } from 'react'

import {
  REVIEW_QUEUE_DEFAULTS,
  useReviewQueue,
} from '../../../api/reconciliation-v2-api-client'
import type {
  ReviewQueueCounts,
  ReviewQueueFilter,
  ReviewQueueItem,
} from '../../../api/reconciliation-v2-api-client'
import { currentQuery, replaceOwnedQuery } from '../replaceOwnedQuery'
import {
  buildPanelQuery,
  readOpenTarget,
  readPanelUrlState,
  RQ_OPEN_KEY,
  type PanelUrlContract,
  type ReviewQueueOpenTarget,
} from './url-state'

/**
 * Search / filter / page state plus the query for ONE Required-action tab.
 *
 * The two tabs paginate independently and their filter vocabularies are
 * disjoint (spec D7), so each owns a separate instance of this — nothing is
 * shared between them but the wire shape of the request.
 *
 * State round-trips through the URL's `rq*` namespace (see ./url-state), which
 * is what makes it survive: switching to Benefits or Transaction Details
 * UNMOUNTS this panel, so anything held only in React state is gone. Each
 * instance owns exactly the three keys in its own contract, so the sibling
 * tab's params — and `rqOpen`, and the Benefits / `td*` params — come
 * through a write untouched.
 *
 * Absent from the URL, state falls back to REVIEW_QUEUE_DEFAULTS, so a
 * freshly-mounted tab at its defaults hashes to the same React Query key as
 * `useReviewQueueCount(kind)`: the two tab label counts and the open tab's
 * first page collapse into one request per kind. A tab
 * SEEDED away from those defaults by an inbound link diverges on purpose — its
 * own page costs its own request, and the counts stay the unfiltered grand
 * totals they are labelled as.
 *
 * Page size is deliberately not URL-synced: `limit` resets to the default on
 * every mount, matching both sibling panels (spec D6). So a seeded `page` is
 * interpreted against the default page size, and a page past the end of the
 * result set fetches an empty page — antd's Pagination clamps the display only.
 * Both siblings behave the same way; clamping here would need the total, which
 * arrives after the request the page number is for.
 *
 * `search` is the live input; `appliedSearch` is what has actually been
 * submitted — the query, and the URL, only move on Enter (or clear), so typing
 * doesn't fire a request or a history write per keystroke.
 *
 * It also holds the `rqOpen` one-shot, because everything the deep link needs
 * is here: the arm it belongs to, the seeded state that puts its row on page
 * one, and — the part a consumer cannot see from outside — whether the request
 * for that page has actually settled. ./useRowDeepLink turns what this offers
 * into an opened modal.
 */

export interface ReviewQueuePanelState<F extends ReviewQueueFilter> {
  search: string
  setSearch: (value: string) => void
  /** Submit the typed search — resets to page 1. */
  applySearch: () => void
  clearSearch: () => void
  filter: F
  /** Change the filter chip — resets to page 1. */
  changeFilter: (filter: F) => void
  page: number
  limit: number
  onPaginationChange: (page: number, pageSize: number) => void
  items: ReviewQueueItem[]
  /** Row count for the CURRENT filter + search — governs pagination only. */
  total: number
  /**
   * Row count per filter chip, scoped to this tab's `kind` and applied
   * search but NOT to its filter — so the chips keep showing where the work is
   * while one of them is selected. `undefined` means the response carried no
   * map at all (a backend predating issue 01, or a failed request), which the
   * chips render as no counts rather than as zeroes.
   *
   * Distinct from {@link total} on purpose: that one IS filter-scoped, and is
   * the pager's number. Swapping them breaks the page count.
   */
  counts: ReviewQueueCounts | undefined
  isFetching: boolean
  isLoading: boolean
  /** Nothing to show at all; a background refetch still holds the last page. */
  showFullError: boolean
  showSkeleton: boolean
  /**
   * The row an inbound `rqOpen` points at — offered ONLY once this panel's own
   * page has settled, and only until {@link consumeOpen}.
   *
   * The settle guard is the whole point of routing this through the hook.
   * `useReviewQueue` keeps the PREVIOUS query's rows on screen while a new one
   * is in flight (`keepPreviousData`), so a consumer matching against
   * {@link items} too early either gives up on a row that was about to arrive
   * or opens one from the page before. Stays `undefined` while the first load
   * has produced no data at all, so a failed request leaves the link in the URL
   * for a reload to retry rather than silently eating it.
   */
  pendingOpen: ReviewQueueOpenTarget | undefined
  /**
   * Take the one-shot: stop offering it and clear `rqOpen` from the URL.
   * Called whether or not the row was found — a link to work a colleague has
   * already cleared must not re-fire on every reload.
   */
  consumeOpen: () => void
}

export const useReviewQueuePanel = <F extends ReviewQueueFilter>(
  /**
   * Everything that differs between the two tabs: the `kind` to request, the
   * URL keys to own, the filter vocabulary an inbound link is validated against,
   * and the default (`all`) that dedupes with the count queries. One argument,
   * not two, so the requested arm and the persisted namespace cannot drift.
   */
  contract: PanelUrlContract<F>
): ReviewQueuePanelState<F> => {
  // Seed from the URL on mount only. Later edits drive React state directly and
  // the effect below mirrors them back, so re-reading the URL would be a loop.
  const [initial] = useState(() => readPanelUrlState(currentQuery(), contract))

  // The one-shot deep link, read against THIS contract so a panel can only ever
  // be handed a row from its own arm. `initial` above has already neutralised
  // the rest of the namespace around it (filter cleared, page 1, search seeded
  // with the searchable id) — see readPanelUrlState.
  const [openTarget, setOpenTarget] = useState<
    ReviewQueueOpenTarget | undefined
  >(() => readOpenTarget(currentQuery(), contract))

  const [search, setSearch] = useState(initial.search)
  const [appliedSearch, setAppliedSearch] = useState(initial.search)
  const [filter, setFilter] = useState<F>(initial.filter)
  const [page, setPage] = useState<number>(initial.page)
  const [limit, setLimit] = useState<number>(REVIEW_QUEUE_DEFAULTS.limit)

  // `isLoading` is true only on the very first load; background refetches keep
  // prior data via keepPreviousData and only flip `isFetching`, so rows stay on
  // screen while updating.
  const { data, isFetching, isLoading, isError } = useReviewQueue({
    kind: contract.kind,
    // Must stay `|| undefined` rather than `?? ''`: React Query hashes a key
    // with a key-sorted JSON.stringify, which drops `undefined`, and that is
    // the whole reason this query and useReviewQueueCount's collapse into one
    // request at the defaults. Pinned by ./url-persistence.test.tsx.
    search: appliedSearch || undefined,
    filter,
    page,
    limit,
  })

  // Mirror the applied state back into the URL, replacing rather than pushing so
  // filter tweaks don't spam browser history. The first run is skipped so an
  // inbound link is neither rewritten nor normalised on mount — which does mean
  // a junk value that was ignored during seeding lingers in the URL until the
  // first edit, exactly as a bad `tdSortBy` does on Transaction Details.
  const isFirstUrlSync = useRef(true)
  useEffect(() => {
    if (isFirstUrlSync.current) {
      isFirstUrlSync.current = false
      return
    }
    replaceOwnedQuery(
      contract.ownedKeys,
      // `appliedSearch`, never `search` — the live input must not touch the URL.
      buildPanelQuery({ filter, search: appliedSearch, page }, contract)
    )
    // `contract` is a module-level const per tab, so listing it costs
    // nothing and keeps the deps honest.
  }, [appliedSearch, filter, page, contract])

  // Clear the one-shot AND record the state it seeded, in one write.
  //
  // The clear alone would leave the URL describing a different list from the
  // one on screen: seeding happens at READ time and the sync effect below
  // skips its first run, so the deep link's search never reaches the URL and
  // the filter/page it overrode never leave it. A reload would then show
  // something else — exactly the view/URL divergence this namespace exists to
  // prevent. The `tdOpen` precedent this ticket names writes the cleared keys
  // for the same reason (AdjustPaymentModal's jump into Transaction Details).
  //
  // `rqOpen` stays owned by nobody: `ownedKeys` is unchanged, and this write
  // claims the key ad hoc exactly as that precedent claims its own wide list.
  const consumeOpen = useCallback(() => {
    setOpenTarget(undefined)
    replaceOwnedQuery(
      [RQ_OPEN_KEY, ...contract.ownedKeys],
      buildPanelQuery({ filter, search: appliedSearch, page }, contract)
    )
  }, [appliedSearch, filter, page, contract])

  // Abandon the one-shot if the operator edits the state it was seeded into
  // before it resolves. Past that point the page on screen is theirs, not the
  // link's, and opening a modal over it would be a jump they did not ask for.
  useEffect(() => {
    if (!openTarget) return
    if (
      appliedSearch !== initial.search ||
      filter !== initial.filter ||
      page !== initial.page
    ) {
      consumeOpen()
    }
  }, [openTarget, appliedSearch, filter, page, initial, consumeOpen])

  const showFullError = isError && !data

  return {
    search,
    setSearch,
    applySearch: () => {
      setAppliedSearch(search)
      setPage(1)
    },
    clearSearch: () => {
      setSearch('')
      setAppliedSearch('')
      setPage(1)
    },
    filter,
    changeFilter: (next) => {
      setFilter(next)
      setPage(1)
    },
    page,
    limit,
    onPaginationChange: (nextPage, nextSize) => {
      setPage(nextPage)
      if (nextSize !== limit) setLimit(nextSize)
    },
    items: data?.data ?? [],
    total: data?.total ?? 0,
    // Held by keepPreviousData across a filter change, and unchanged by one
    // anyway — so the chips never flicker back to bare while a filtered page
    // is in flight.
    counts: data?.counts,
    isFetching,
    isLoading,
    showFullError,
    showSkeleton: !showFullError && isLoading,
    // Held back until the request for THIS page has settled — see the field's
    // doc comment for why `keepPreviousData` makes that load-bearing.
    pendingOpen:
      openTarget && data && !isLoading && !isFetching ? openTarget : undefined,
    consumeOpen,
  }
}
